import { useTheme, Avatar, Box, Typography, IconButton, AppBar, Popper, ClickAwayListener, Card, Divider, Tooltip, Button } from "@mui/material"
import { Link, useNavigate } from "react-router-dom"
import { useEffect, useState } from "react"
import { observer } from "mobx-react"

import ProfileImageCropAndUploadDialog from "../../student/ProfileImageCropAndUploadDialog"
import { useLayoutContext } from "../../../../shared/contexts/layout.context"
import { Icons } from '../../../../shared/utils/icons.jsx'
import { ERPDashboardPageURL } from '../../../../shared/utils/constants.jsx'
import { secondsToHMS } from '../../../../shared/utils/date-time-utilities.jsx'
import { convertToCDNLink } from '../../../../shared/utils/helpers.jsx'
import { useThemeContext } from "../../../../shared/contexts/theme.context"
import { useUserStore } from "../../../store/user.store"
import aliveLogo from '../../../../shared/assets/alivelogo.png'
import simbaImg from '../../../../shared/assets/Habba-Logo.png'
import CustomSwitch from "../../../../shared/components/common/CustomSwitch"
import StaffPushNotifications from '../../../../notifications/StaffPushNotifications.jsx'
import placementTrackerAPI from '../../../../alive-placements/services/api/PlacementTrackerAPI.js'

const themeOptions = [
    { value: 'light', icon: Icons.default.LightMode, label: 'light' },
    { value: 'dark', icon: Icons.default.DarkMode, label: 'dark' },
    { value: 'system', icon: Icons.default.SystemTheme, label: 'system' },
]

const SessionEndCountdown = () => {
    const [timeLeft, setTimeLeft] = useState( null )

    const navigate = useNavigate()

    useEffect( () => {
        const calculateTimeLeft = () => {
            const endsAt = sessionStorage.getItem( 'impersonation_session_ends_at' )

            if ( !endsAt ) return null

            // Convert stored value to a date (handles both ISO strings and timestamps)
            const difference = new Date( endsAt ) - new Date()

            if ( difference <= 0 ) {
                sessionStorage.removeItem( 'impersonation_id_token' )
                sessionStorage.removeItem( 'is_impersonation' )
                sessionStorage.removeItem( 'impersonator_details' )
                sessionStorage.removeItem( 'impersonation_session_ends_at' )
                return window.location.href = '/admin/impersonation'
            }

            return difference
        }

        // Initial check
        setTimeLeft( calculateTimeLeft() )

        // Update every second
        const timer = setInterval( () => {
            const remaining = calculateTimeLeft()
            setTimeLeft( remaining )

            if ( remaining === 0 ) {
                clearInterval( timer )
                // Optional: Force a logout or refresh when timer hits zero
                // window.location.reload(); 
            }
        }, 1000 )

        return () => clearInterval( timer )
    }, [] )

    return (
        <Typography
            variant="caption"
            sx={{
                fontSize: "10px",
                display: "block",
                fontWeight: 'bold',
                color: timeLeft < 60000 ? 'error.main' : 'inherit'
            }}>
            <Typography
                component="span"
                sx={{
                    display: { md: "inline", xs: "none" },
                    fontSize: "inherit",
                    fontWeight: "inherit",
                    color: 'inherit'
                }}>Ends In:</Typography> {secondsToHMS( timeLeft / 1000 )}
        </Typography>
    );
}

const Navbar = observer( () => {

    const [settingsAnchorEl, setSettingsAnchorEl] = useState( null )
    const [selectedProfileImg, setSelectedProfileImg] = useState( null )
    const [profile, setProfile] = useState( null )

    const UserStore = useUserStore()
    const { navHeight, navHeadText, active } = useLayoutContext()

    const navigate = useNavigate()

    const { palette, shadows, border } = useTheme()
    const { setSelectedTheme, selectedTheme } = useThemeContext()

    const isImpersonation = sessionStorage.getItem( "is_impersonation" ) === 'true'

    const handleThemeChange = value => {
        setSelectedTheme( value )
        localStorage.setItem( 'preferedTheme', value )
    }

    const toggleAuditMode = () => {
        if ( active.includes( '/audit' ) ) {
            let redirect = localStorage.getItem( 'audit_redirect' )
            if ( !redirect )
                redirect = ["ADMIN", "SUBADMIN"].includes( UserStore.getUser.user_role?.toUpperCase() ) ? `/admin/home` : `/faculty/home`
            navigate( redirect )
        } else {
            localStorage.setItem( 'audit_redirect', `${active}${window.location.search}` )
            navigate( '/audit/' )
        }
    }

    // const logout = async () => {
    //     await AuthStore.doLogout()
    //     toast.success( "Logged out successfully!" )
    //     navigate( '/' )
    // }

    useEffect( () => {
        setProfile( UserStore.getUser.user_profile )
    }, [UserStore] )

    useEffect( () => {
        if ( !navHeadText ) return
        document.title = `Alive | Digital classrooms | ${navHeadText || ""}`
    }, [navHeadText] )


    return (
        <AppBar
            id="appbar"
            elevation={0}
            sx={{
                borderRadius: "0",
                background: "transparent",
                transition: "0.2s ease-out",
                position: "static",
                color: palette.nav.font,
                height: { sm: "50px", xs: "60px" },
                zIndex: "200"
            }}>

            <Box
                id="app-bar"
                sx={{
                    alignItems: "center",
                    justifyContent: "space-between",
                    height: `${navHeight}px`,
                    padding: { sm: "0 20px 0 0", xs: "0 10px" },
                    display: "flex",
                    maxWidth: "100%",
                    transition: "0.2s ease-out"
                }}>
                <Box
                    sx={{
                        display: "flex",
                        gap: "10px",
                        alignItems: "center"
                    }}>
                    {/* <IconButton sx={{ color: palette.nav.font, display: { md: "none", xs: "flex" } }} size="small" onClick={menuState ? closeSideMenu : openSideMenu} >
                        <MenuIcon />
                    </IconButton> */}
                    <Box
                        sx={{
                            padding: "2px",
                            display: { sm: "none", xs: "flex" },
                            alignItems: "center",
                            justifyContent: "center",
                            borderRadius: "5px",
                            bgcolor: "white",
                            aspectRatio: "1 / 1"
                        }}><Box component="img" src={aliveLogo} alt="Logo" sx={{
                        width: "35px"
                    }} /></Box>
                    <Box
                        component="img"
                        src={simbaImg}
                        sx={{
                            display: { sm: "block", xs: "none" },
                            width: "20px"
                        }} />
                    <Typography
                        component='h1'
                        color={palette.nav.font}
                        sx={{
                            display: { sm: "block", xs: "none" },
                            fontSize: "14px"
                        }}>
                        Acharya LMS
                    </Typography>
                    <Typography
                        component='span'
                        color={palette.nav.font}
                        sx={{
                            display: { sm: "block", xs: "none" },
                            fontSize: "14px"
                        }}>
                        |
                    </Typography>
                    <Typography color={palette.nav.font} component="span" sx={{
                        fontSize: { md: "14px", xs: "14px" }
                    }} >{navHeadText}</Typography>
                </Box>
                {/* <Box display="flex" gap="10px">
                    <Box component="img" src={simbaImg} width="20px" />
                    <Typography fontSize="14px" color={palette.nav.font} >
                        Welcome to Acharya Learning Management System
                    </Typography>
                </Box> */}
                {<Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px"
                    }}>
                    {( ["PRINCIPAL", "HOD", "ADMIN", "SUBADMIN"].includes( UserStore.getUser.user_role?.toUpperCase() ) || UserStore.getUser.user_auditor ) && <Box
                        onClick={toggleAuditMode}
                        sx={{
                            justifyContent: "space-between",
                            display: { sm: "flex", xs: "none" },
                            gap: "10px",
                            alignItems: "center",
                            padding: "5px 10px",
                            boxShadow: shadows[1],
                            bgcolor: palette.contentBg,
                            border: border[1],
                            borderRadius: "10px",
                            cursor: "pointer"
                        }}>
                        <Typography
                            color={palette.common.font}
                            variant="subtitle2"
                            sx={{
                                fontSize: "12px",
                                lineHeight: "0"
                            }}>Audit Mode</Typography>
                        <CustomSwitch size="small" checked={active.startsWith( '/audit' )} onChange={toggleAuditMode} />
                    </Box>}
                    {/* <IconButton size='small' sx={{ color: palette.nav.font }} onClick={( e ) => setSettingsAnchorEl( e.currentTarget )}>
                        {Icons.default.SettingsOutlined}
                    </IconButton> */}

                    {isImpersonation && <Box
                        sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            justifyContent: "center",
                            minHeight: "15px",
                            gap: { md: "10px", xs: "2px" },
                            bgcolor: palette.customThemeColor.light + "22",
                            alignItems: "center",
                            border: `1px solid ${palette.customThemeColor.main}`,
                            padding: { md: "2px 10px", xs: "5px 10px" },
                            borderRadius: "20px"
                        }}>
                        {Icons.default.Impersonation}
                        <Box>
                            <Typography
                                variant="subtitle2"
                                sx={{
                                    display: { md: "block", xs: "none" },
                                    fontSize: "12px"
                                }}>Impersonating</Typography>
                            <SessionEndCountdown />
                        </Box>
                    </Box>}

                    <StaffPushNotifications
                        apiClient={placementTrackerAPI.apiClient}
                        currentUser={UserStore.getUser}
                    />

                    <Avatar onClick={( e ) => setSettingsAnchorEl( e.currentTarget )} src={convertToCDNLink( profile )} sx={{ height: "26px", width: "26px", cursor: "pointer" }}></Avatar>

                    <Popper sx={{ zIndex: 100 }} placement='bottom' open={Boolean( settingsAnchorEl )} anchorEl={settingsAnchorEl}>
                        <ClickAwayListener onClickAway={() => setSettingsAnchorEl( null )}>
                            <Card sx={{ marginTop: "10px", marginRight: "20px", border: border[1], borderRadius: "5px" }} elevation={1} >
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "5px",
                                        padding: "5px"
                                    }}>
                                    <Avatar onClick={( e ) => setSettingsAnchorEl( e.currentTarget )} src={convertToCDNLink( profile )} sx={{ height: "30px", width: "30px", borderRadius: "5px", cursor: "pointer" }}></Avatar>
                                    <Box>
                                        <Typography
                                            variant='subtitle2'
                                            sx={{
                                                display: "block",
                                                lineHeight: 1.3,
                                                fontSize: "14px",
                                                textTransform: "capitalize"
                                            }}>{UserStore.getUser["employee_name"] || UserStore.getUser["user_name"] || ""}</Typography>
                                        <Typography
                                            color='textSecondary'
                                            variant='subtitle2'
                                            sx={{
                                                display: "block",
                                                lineHeight: 1.3,
                                                fontSize: "12px"
                                            }}>{UserStore.getUser["AUID"] || UserStore.getUser["empcode"] || UserStore.getUser["user_id"] || ""}</Typography>
                                    </Box>
                                </Box>
                                <Divider />
                                <Box
                                    sx={{
                                        padding: "5px",
                                        justifyContent: "space-between",
                                        display: "flex",
                                        gap: "20px",
                                        alignItems: "center"
                                    }}>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            gap: "10px",
                                            alignItems: "center"
                                        }}>
                                        {Icons.default.PaletteOutlined}
                                        <Typography
                                            variant="variant2"
                                            sx={{
                                                width: "max-content",
                                                display: "flex",
                                                fontSize: "12px"
                                            }}>
                                            Theme
                                        </Typography>
                                    </Box>
                                    <Box
                                        sx={{
                                            borderRadius: "20px",
                                            bgcolor: palette.contentBg,
                                            boxShadow: `inset 0px 0px 3px 0px ${palette.primary.light + "44"}`,
                                            padding: "5px",
                                            display: "flex",
                                            gap: "2px",
                                            position: "relative",
                                            alignItems: "center"
                                        }}>

                                        <Box
                                            sx={{
                                                position: "absolute",
                                                bgcolor: palette.background.paper,
                                                borderRadius: "50%",
                                                top: "5px",
                                                bottom: "5px",
                                                left: `${5 + ( themeOptions.findIndex( to => to.value === selectedTheme ) * 30 )}px`,
                                                height: "calc(100% - 10px)",
                                                aspectRatio: "1 / 1",
                                                transition: "all 300ms"
                                            }} />

                                        {themeOptions.map( theme => (
                                            <Tooltip disableInteractive key={theme.value} title={theme.label} >
                                                <IconButton onClick={() => handleThemeChange( theme.value )} size='small' >
                                                    {theme.icon}
                                                </IconButton>
                                            </Tooltip>
                                        ) )}
                                    </Box>
                                </Box>
                                <Divider />
                                <Box sx={{
                                    padding: "5px"
                                }}>
                                    <Link to={ERPDashboardPageURL}  ><Button startIcon={Icons.default.Logout} color='error' size='small' fullWidth variant='contained' disableElevation >Back to ERP</Button></Link>
                                </Box>
                            </Card>
                        </ClickAwayListener>
                    </Popper>

                    {/* <Popover slotProps={{ paper: { sx: { minWidth: "180px", border: `1px solid ${palette.borderColor}` } } }} sx={{ maxWidth: "300px" }} onClose={e => { setAnchorEl( null ) }} transformOrigin={{ horizontal: 'right', vertical: 'top' }} anchorOrigin={{ horizontal: 'left', vertical: 'top' }} anchorEl={anchorEl} open={open}>

                        <input onChange={handleProfileImgSelection} type="file" accept="image/jpeg" name="profile" id="profile" className="invisible-element" ref={profileImgInputRef} />

                        <Box display="flex" alignItems="center" flexDirection="column" gap="20px" padding="20px">
                            <Badge
                                overlap="circular"
                                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                badgeContent={
                                    <Avatar onClick={() => profileImgInputRef.current.click()} sx={{ bgcolor: "primary.main", width: 20, height: 20, cursor: "pointer" }}>
                                        <Edit sx={{ fontSize: "15px" }} />
                                    </Avatar>
                                }
                            >
                                <Avatar onClick={() => profileImgInputRef.current.click()} sx={{ cursor: "pointer", border: `4px solid ${palette.borderColor}` }} alt={UserStore.getUser["user_name"] ? UserStore.getUser["user_name"] : ""} src={profile ? convertToCDNLink( profile ) : ""} >
                                    {( UserStore.getUser["user_name"] ? UserStore.getUser["user_name"] : "" ).substring( 0, 1 )}
                                </Avatar>
                            </Badge>
                            <Box>
                                <Typography marginBottom="-5px" textAlign="center" variant='subtitle1' textTransform="capitalize">{UserStore.getUser["user_name"] ? UserStore.getUser["user_name"] : ""}</Typography>
                                <Typography color="textSecondary" fontSize="10px" textAlign="center" variant='subtitle1' textTransform="capitalize">{UserStore.getUser["user_role"] ? UserStore.getUser["user_id"] : ""}</Typography>
                                <Typography color="textSecondary" fontSize="10px" textAlign="center" variant='subtitle1' textTransform="capitalize">{UserStore.getUser["user_role"] ? UserStore.getUser["user_role"] : ""}</Typography>
                            </Box>
                            <Button color="error" onClick={logout} startIcon={<LogoutIcon sx={{ color: "error.main" }} fontSize="small" />}>
                                Logout
                            </Button>
                        </Box>
                    </Popover> */}

                    <ProfileImageCropAndUploadDialog setProfile={setProfile} setImageSrc={setSelectedProfileImg} imageSrc={selectedProfileImg} />
                </Box>}
            </Box>
        </AppBar>
    );
} )

export default Navbar
